import { Loader } from '@mantine/core';
import { UseQueryResult } from '@tanstack/react-query';
import { PlusCircleSVG, RefreshSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import { NavigatorParams } from 'common/constants/navigator-params';
import StyleBreakpoint from 'common/styles/styles.breakpoint';
import stylesColors from 'common/styles/styles.colors';
import { themedStyled } from 'common/styles/styletron.theme';
import Separator from 'components/common/separator';
import { LeftActionContainer } from 'legacy-modules/styles';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import { Button } from './buttons';
import Text from './text';

interface Props<T extends UseQueryResult> {
  onRefresh?: () => void;
  onCreate?: () => void;
  title?: string;
  subtitle?: string;
  showRefresh?: boolean;
  showAdd?: boolean;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  rightCustomComponent?: React.ReactNode;
  leftCustomComponent?: React.ReactNode;
  showBorder?: boolean;
  query: T;
  linkAdd?: NavigationRoutes;
}

export default function ListHeader<T extends UseQueryResult>(props: Props<T>) {
  const { t } = useTranslation();

  const {
    title,
    subtitle,
    onRefresh,
    onCreate,
    rightCustomComponent,
    leftCustomComponent,
    showRefresh = true,
    showAdd = true,
    showBorder = false,
    query,
    linkAdd,
  } = props;

  const addComponent = (
    <Button
      onClick={
        typeof linkAdd === 'undefined'
          ? () => {
              onCreate?.();
            }
          : () => {
              NavigatorParams.remove(linkAdd);
            }
      }
      startEnhancer={(color, size) => (
        <PlusCircleSVG size={size} color={color} />
      )}
      style={styles.button}
    >
      {t('common:add')}
    </Button>
  );

  return (
    <>
      <TopContainer>
        <LeftActionContainer margin={showRefresh}>
          <TextContainer>
            {!!title && <Text variant="HeadingXSmall">{title}</Text>}
            {!!subtitle && (
              <>
                <div style={{ marginTop: 4 }} />
                <Text
                  $style={{
                    color: stylesColors.skyDarkest,
                  }}
                  variant="body3Semibold"
                >
                  {subtitle}
                </Text>
              </>
            )}
          </TextContainer>
          {leftCustomComponent && <>{leftCustomComponent}</>}
        </LeftActionContainer>
        <RightContainer>
          {showRefresh && (
            <Button
              onClick={() => {
                onRefresh?.();
              }}
              startEnhancer={(color, size) => {
                if (query.isFetching) {
                  return <Loader color={color} size={size} />;
                }
                return <RefreshSVG color={color} size={size} />;
              }}
              buttonType="secondary"
              style={styles.button}
              disabled={query.isFetching}
            >
              {t('common:refresh')}
            </Button>
          )}
          {showAdd && (
            <>
              <Separator gap={16} />
              {linkAdd ? (
                <Link passHref href={linkAdd}>
                  {addComponent}
                </Link>
              ) : (
                addComponent
              )}
            </>
          )}
          {!!rightCustomComponent && (
            <>
              <Separator gap={16} />
              {rightCustomComponent}
            </>
          )}
        </RightContainer>
      </TopContainer>
      {showBorder && <FilterDivider showBorder />}
    </>
  );
}

const TopContainer = themedStyled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',

  [`@media screen and (max-width: calc(${StyleBreakpoint.lg} - 1px))`]: {
    flexDirection: 'column',
    alignItems: 'unset',
  },
});

const FilterDivider = themedStyled<'div', { showBorder?: boolean }>(
  'div',
  ({ $theme, showBorder = true }) => ({
    ...(showBorder
      ? {
          border: `1px solid ${$theme.colors.skyDark}`,
          margin: '16px 0px',
        }
      : {
          marginBottom: '16px',
        }),
  }),
);

// const FilterContainer = themedStyled('div', {
//   marginBottom: '16px',
// });

const styles = {
  button: {
    // padding: '12px ',
    minWidth: '120px',
    // padding: '6px 24px',
  },
};

const RightContainer = themedStyled('div', ({ $theme }) => ({
  display: 'flex',
  [$theme.mediaQuery.maxLg]: {
    justifyContent: 'flex-end',
  },
}));

const TextContainer = themedStyled('div', {});
